import { initializeApp } from 'firebase/app';
import { getFirestore, doc, setDoc, collection, addDoc, getDoc, updateDoc, deleteDoc, getDocs, query, orderBy, collectionGroup, where, limit } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged 
} from 'firebase/auth';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

const app = initializeApp(firebaseConfig);
export const db = getFirestore(app);
export const auth = getAuth(app);

// Sample quiz data
const createSampleQuiz = async (userId) => {
  const quizRef = await addDoc(collection(db, `users/${userId}/quizzes`), {
    title: "サンプルクイズ - プログラミング基礎",
    description: "プログラミングの基本的な概念についてのクイズです",
    createdAt: new Date().toISOString(),
    published: false,
    visibility: "private",
    authorId: userId
  });

  // Add sample question
  await addDoc(collection(db, `users/${userId}/quizzes/${quizRef.id}/questions`), {
    questionText: "次のうち、JavaScriptの変数宣言に使用されるキーワードはどれですか？",
    questionImageUrl: null,
    tags: ["JavaScript", "変数", "プログラミング"],
    order: 1,
    questionType: "single",
    choices: [
      { id: "a", text: "var", choiceImageUrl: null, isCorrect: true },
      { id: "b", text: "string", choiceImageUrl: null, isCorrect: false },
      { id: "c", text: "function", choiceImageUrl: null, isCorrect: false },
      { id: "d", text: "return", choiceImageUrl: null, isCorrect: false }
    ],
    explanation: "JavaScriptでは、var、let、constが変数宣言に使用されます。この中でvarが最も基本的なキーワードです。",
    explanationImageUrl: null
  });
};

// Create user profile and sample quiz
const createUserProfile = async (user) => {
  await setDoc(doc(db, 'users', user.uid), {
    displayName: user.uid,
    registeredAt: new Date().toISOString()
  });
  
  await createSampleQuiz(user.uid);
};

// Authentication functions
export const signUp = async (email, password) => {
  const userCredential = await createUserWithEmailAndPassword(auth, email, password);
  await createUserProfile(userCredential.user);
  return userCredential;
};

export const signIn = (email, password) => 
  signInWithEmailAndPassword(auth, email, password);

export const logOut = () => signOut(auth);

// IDトークン（JWT）やメールアドレスはコンソールに出力しない
// （トークンが漏れると有効期限内は本人になりすましてAPIを呼び出せるため）
export const onAuthStateChange = (callback) =>
  onAuthStateChanged(auth, callback);

// Quiz creation functions
export const createQuiz = async (userId, quizData) => {
  try {
    // Create quiz document
    const quizRef = await addDoc(collection(db, `users/${userId}/quizzes`), {
      title: quizData.title,
      description: quizData.description,
      createdAt: new Date().toISOString(),
      published: quizData.published,
      visibility: quizData.visibility,
      authorId: userId
    });

    // Add questions to the quiz
    for (const question of quizData.questions) {
      await addDoc(collection(db, `users/${userId}/quizzes/${quizRef.id}/questions`), {
        questionText: question.questionText,
        questionImageUrl: question.questionImageUrl || null,
        tags: question.tags || [],
        order: question.order,
        questionType: question.questionType,
        choices: question.choices,
        explanation: question.explanation || null,
        explanationImageUrl: question.explanationImageUrl || null
      });
    }

    return quizRef.id;
  } catch (error) {
    console.error('Error creating quiz:', error);
    throw error;
  }
};

// Get quiz by ID
export const getQuiz = async (userId, quizId) => {
  try {
    const quizRef = doc(db, `users/${userId}/quizzes/${quizId}`);
    const quizSnap = await getDoc(quizRef);
    
    if (quizSnap.exists()) {
      return {
        id: quizSnap.id,
        ...quizSnap.data()
      };
    } else {
      throw new Error('Quiz not found');
    }
  } catch (error) {
    console.error('Error getting quiz:', error);
    throw error;
  }
};

// Update quiz
export const updateQuiz = async (userId, quizId, quizData) => {
  try {
    const quizRef = doc(db, `users/${userId}/quizzes/${quizId}`);
    await updateDoc(quizRef, {
      title: quizData.title,
      description: quizData.description,
      published: quizData.published,
      visibility: quizData.visibility,
      updatedAt: new Date().toISOString()
    });
  } catch (error) {
    console.error('Error updating quiz:', error);
    throw error;
  }
};

// Get questions for a quiz
export const getQuizQuestions = async (userId, quizId) => {
  try {
    const questionsRef = collection(db, `users/${userId}/quizzes/${quizId}/questions`);
    const q = query(questionsRef, orderBy('order', 'asc'));
    const querySnapshot = await getDocs(q);
    
    const questions = [];
    querySnapshot.forEach((doc) => {
      questions.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return questions;
  } catch (error) {
    console.error('Error getting quiz questions:', error);
    throw error;
  }
};

// Add question to quiz
export const addQuestionToQuiz = async (userId, quizId, questionData) => {
  try {
    const questionsRef = collection(db, `users/${userId}/quizzes/${quizId}/questions`);
    const questionRef = await addDoc(questionsRef, {
      questionText: questionData.questionText,
      questionImageUrl: questionData.questionImageUrl || null,
      tags: questionData.tags || [],
      order: questionData.order,
      questionType: questionData.questionType,
      choices: questionData.choices,
      explanation: questionData.explanation || null,
      explanationImageUrl: questionData.explanationImageUrl || null
    });
    
    return questionRef.id;
  } catch (error) {
    console.error('Error adding question:', error);
    throw error;
  }
};

// Update question
export const updateQuestion = async (userId, quizId, questionId, questionData) => {
  try {
    const questionRef = doc(db, `users/${userId}/quizzes/${quizId}/questions/${questionId}`);
    await updateDoc(questionRef, {
      questionText: questionData.questionText,
      questionImageUrl: questionData.questionImageUrl || null,
      tags: questionData.tags || [],
      order: questionData.order,
      questionType: questionData.questionType,
      choices: questionData.choices,
      explanation: questionData.explanation || null,
      explanationImageUrl: questionData.explanationImageUrl || null
    });
  } catch (error) {
    console.error('Error updating question:', error);
    throw error;
  }
};

// Delete question
export const deleteQuestion = async (userId, quizId, questionId) => {
  try {
    const questionRef = doc(db, `users/${userId}/quizzes/${quizId}/questions/${questionId}`);
    await deleteDoc(questionRef);
  } catch (error) {
    console.error('Error deleting question:', error);
    throw error;
  }
};

// Delete entire quiz with all questions and attempts
export const deleteQuiz = async (userId, quizId) => {
  try {
    // First, delete all questions in the quiz
    const questionsRef = collection(db, `users/${userId}/quizzes/${quizId}/questions`);
    const questionsSnapshot = await getDocs(questionsRef);
    
    const deletePromises = [];
    questionsSnapshot.forEach((questionDoc) => {
      deletePromises.push(deleteDoc(questionDoc.ref));
    });
    
    // Also delete all attempts for this quiz
    const attemptsRef = collection(db, `users/${userId}/quizzes/${quizId}/attempts`);
    const attemptsSnapshot = await getDocs(attemptsRef);
    
    attemptsSnapshot.forEach((attemptDoc) => {
      deletePromises.push(deleteDoc(attemptDoc.ref));
    });
    
    // Wait for all questions and attempts to be deleted
    await Promise.all(deletePromises);
    
    // Then delete the quiz itself
    const quizRef = doc(db, `users/${userId}/quizzes/${quizId}`);
    await deleteDoc(quizRef);
    
  } catch (error) {
    console.error('Error deleting quiz:', error);
    throw error;
  }
};

// Save quiz attempt (results)
export const saveQuizAttempt = async (userId, quizId, attemptData) => {
  try {
    const attemptsRef = collection(db, `users/${userId}/quizzes/${quizId}/attempts`);
    const attemptRef = await addDoc(attemptsRef, {
      userId: attemptData.userId,
      startedAt: attemptData.startedAt,
      finishedAt: attemptData.finishedAt,
      answers: attemptData.answers,
      score: attemptData.score
    });
    
    return attemptRef.id;
  } catch (error) {
    console.error('Error saving quiz attempt:', error);
    throw error;
  }
};

// Get quiz attempts for a specific quiz
export const getQuizAttempts = async (userId, quizId) => {
  try {
    const attemptsRef = collection(db, `users/${userId}/quizzes/${quizId}/attempts`);
    const q = query(attemptsRef, orderBy('finishedAt', 'desc'));
    const querySnapshot = await getDocs(q);
    
    const attempts = [];
    querySnapshot.forEach((doc) => {
      attempts.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return attempts;
  } catch (error) {
    console.error('Error getting quiz attempts:', error);
    throw error;
  }
};

// Bulk import questions to a quiz
export const importQuestionsToQuiz = async (userId, quizId, questionsData) => {
  try {
    const questionsRef = collection(db, `users/${userId}/quizzes/${quizId}/questions`);
    
    // Import all questions in parallel
    const importPromises = questionsData.map(async (questionData) => {
      const questionRef = await addDoc(questionsRef, {
        questionText: questionData.questionText,
        questionImageUrl: questionData.questionImageUrl || null,
        tags: questionData.tags || [],
        order: questionData.order,
        questionType: questionData.questionType,
        choices: questionData.choices,
        explanation: questionData.explanation || null,
        explanationImageUrl: questionData.explanationImageUrl || null
      });
      return questionRef.id;
    });
    
    const questionIds = await Promise.all(importPromises);
    return questionIds;
  } catch (error) {
    console.error('Error importing questions:', error);
    throw error;
  }
};

// Get published quizzes for public list
export const getPublicQuizzes = async (limitCount = 50) => {
  try {
    const quizzesRef = collectionGroup(db, 'quizzes');
    const q = query(
      quizzesRef,
      where('visibility', '==', 'public'),
      where('published', '==', true),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );
    
    const querySnapshot = await getDocs(q);
    const quizzes = [];
    
    querySnapshot.forEach((doc) => {
      // id / userId はドキュメントのパスから決定する（他ユーザーが書き込んだフィールドで上書きされないよう最後に設定）
      quizzes.push({
        ...doc.data(),
        id: doc.id,
        userId: doc.ref.parent.parent.id // Extract userId from the path
      });
    });

    // 各クイズの作成者のdisplayNameを並行して取得
    const quizzesWithDisplayNames = await Promise.all(
      quizzes.map(async (quiz) => {
        try {
          const userRef = doc(db, 'users', quiz.userId);
          const userSnap = await getDoc(userRef);
          
          if (userSnap.exists()) {
            const userData = userSnap.data();
            return {
              ...quiz,
              displayName: userData.displayName || quiz.userId
            };
          } else {
            return {
              ...quiz,
              displayName: quiz.userId
            };
          }
        } catch (error) {
          console.warn(`Error fetching displayName for user ${quiz.userId}:`, error);
          return {
            ...quiz,
            displayName: quiz.userId
          };
        }
      })
    );
    
    return quizzesWithDisplayNames;
  } catch (error) {
    console.error('Error getting published quizzes:', error);
    throw error;
  }
};